import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import {
  CardPackCurrency,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { CloseButton } from '@sorare/core/src/atoms/buttons/CloseButton';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { GemIcon } from '@sorare/core/src/atoms/icons/GemIcon';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BrandS } from '@sorare/core/src/atoms/typography';
import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { EssenceBalance } from '@sorare/core/src/components/balance/EssenceBalance';
import { CommonEssenceIcon } from '@sorare/core/src/components/commonGame/CommonEssenceIcon';
import { GemsBalance } from '@sorare/core/src/components/commonGame/GemsBalance';
import { Dialog } from '@sorare/core/src/components/dialog';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { ANY_SPORT_MY_CARDS_CARDS_TYPE } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';
import { getCommonCardShardsCount } from '@sorare/core/src/lib/currentUser';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { glossary } from '@sorare/core/src/lib/glossary';
import { OpenBuyGemsDialog_Context } from '@sorare/core/src/protos/events/platform/web/events';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import type { BuyCardPackFlow_cardPack } from './__generated__/index.graphql';

const Root = styled(Vertical)`
  min-height: 100%;
  padding: var(--double-and-a-half-unit) var(--double-unit) 0;
  background: linear-gradient(
      to bottom,
      transparent 0%,
      transparent 70%,
      var(--secondary-color) 100%
    ),
    linear-gradient(to bottom, transparent 0%, var(--secondary-color) 100%),
    linear-gradient(
      to bottom,
      var(--secondary-color) 0%,
      var(--primary-color) 100%
    );
`;
const Content = styled(Vertical)`
  justify-content: center;
  padding: var(--double-unit);
  padding-bottom: var(--quadruple-unit);
  gap: var(--double-unit);
  flex: 1;

  @media ${laptopAndAbove} {
    gap: var(--quadruple-unit);
    min-height: min(calc(100vh - var(--unit) * 12), 600px);
  }
`;
const Img = styled.img`
  aspect-ratio: var(--card-aspect-ratio);
  width: 65%;
  max-width: 240px;
`;
const StyledLoadingButton = styled(LoadingButton)`
  align-self: center;
  && {
    min-width: 200px;
  }
`;
const Warning = styled(Horizontal)`
  width: 100%;
  padding: var(--intermediate-unit) var(--double-unit);
  background: var(--c-surface-caution);
  border-radius: var(--double-unit);
  justify-content: space-between;
  border: 1px solid var(--c-yellow-400);
`;

const Cost = styled(Horizontal)`
  gap: var(--unit);
  font: var(--t-brand-s);
  text-transform: uppercase;
  color: var(--c-nd-600);
`;

type BuyCardPackFlowProps = {
  onClose: () => void;
  cardPack: BuyCardPackFlow_cardPack;
  onPurchase: () => Promise<void>;
};

export const BuyCardPackFlow = ({
  onClose,
  cardPack,
  onPurchase,
}: BuyCardPackFlowProps) => {
  const { pictureUrl, price, currency } = cardPack;
  const track = useEvents();
  const openDialog = useOpenDialog();

  const { currentUser } = useCurrentUserContext();

  const premiumPack = currency === CardPackCurrency.COMMON_GEM;

  const userBalance = premiumPack
    ? currentUser?.gemsBalances?.[0]?.amount || 0
    : getCommonCardShardsCount(currentUser, cardPack.cardSet?.slug) || 0;

  const { generateSportContextPath } = useSportContext();

  const insufficientBalance = userBalance < price;

  return (
    <Dialog
      open
      onClose={onClose}
      hideHeader
      fullWidth
      maxWidth="xs"
      style={
        {
          '--primary-color': cardPack.primaryColor,
          '--secondary-color': cardPack.secondaryColor,
        } as CSSProperties
      }
      disableBackdropClick
    >
      <Root gap={2}>
        <SBHorizontal>
          {premiumPack ? (
            <GemsBalance balance={userBalance} />
          ) : (
            <EssenceBalance
              value={userBalance}
              rarity={Rarity.common}
              cardSetSlug={cardPack.cardSet?.slug}
            />
          )}
          <CloseButton size="medium" onClose={onClose} />
        </SBHorizontal>
        <Content>
          <Vertical center gap={3}>
            <Img
              src={proxyUrl(pictureUrl, { cropWidth: 320 })}
              alt=""
              width={320}
            />
            <Cost color="var(--c-nd-600)">
              <FormattedMessage
                id="BuyCardPackDialog.cost"
                defaultMessage="Cost: {currencyIcon} <span>{cost}</span>"
                values={{
                  cost: price,
                  currencyIcon: premiumPack ? (
                    <GemIcon size={2.5} />
                  ) : (
                    <CommonEssenceIcon width={18} />
                  ),
                  span: chunks => (
                    <BrandS color="var(--c-white)">{chunks}</BrandS>
                  ),
                }}
              />
            </Cost>
          </Vertical>
          <Vertical center gap={2.5}>
            <StyledLoadingButton
              color="primary"
              size="medium"
              onClick={onPurchase}
              disabled={insufficientBalance}
            >
              <FormattedMessage {...glossary.buyNow} />
            </StyledLoadingButton>
            {!!insufficientBalance && (
              <Warning>
                <BodyM color="var(--c-yellow-400)" bold>
                  <Horizontal>
                    <FontAwesomeIcon icon={faInfoCircle} />
                    <FormattedMessage
                      id="commonPaymentBox.insufficientBalance"
                      defaultMessage="Insufficient balance"
                    />
                  </Horizontal>
                </BodyM>
                {premiumPack ? (
                  <Button
                    size="medium"
                    color="secondary"
                    onClick={() => {
                      track('Open Buy Gems Dialog', {
                        context: OpenBuyGemsDialog_Context.PACK_DIALOG,
                      });
                      openDialog(DialogKey.buyGems, 'true');
                    }}
                  >
                    <FormattedMessage
                      id="commonPaymentBox.buyGems"
                      defaultMessage="Buy gems"
                    />
                  </Button>
                ) : (
                  <Button
                    size="medium"
                    color="secondary"
                    to={generateSportContextPath(
                      ANY_SPORT_MY_CARDS_CARDS_TYPE,
                      {
                        params: {
                          type: Rarity.common,
                        },
                      }
                    )}
                  >
                    <FormattedMessage
                      id="commonPaymentBox.scrapCard"
                      defaultMessage="Scrap Card"
                    />
                  </Button>
                )}
              </Warning>
            )}
          </Vertical>
        </Content>
      </Root>
    </Dialog>
  );
};

BuyCardPackFlow.fragments = {
  cardPack: gql`
    fragment BuyCardPackFlow_cardPack on CardPack {
      slug
      cardSet {
        slug
      }
      pictureUrl
      price
      currency
      primaryColor: color(variant: PRIMARY)
      secondaryColor: color(variant: SECONDARY)
    }
  ` as TypedDocumentNode<BuyCardPackFlow_cardPack>,
};
